/**
 * Builds a blank label-sheet PDF of QR stickers and downloads it.
 */
import React from 'react'
import { pdf } from '@react-pdf/renderer'
import { QrStickerBatchPdf, type QrStickerPdfItem } from '../components/qr/QrStickerBatchPdf'

async function qrCodeToDataUrl(code: string): Promise<string> {
  const data = encodeURIComponent('RBCQR:' + code)
  const url =
    'https://api.qrserver.com/v1/create-qr-code/?size=300x300&margin=10&data=' + data
  const res = await fetch(url)
  if (!res.ok) throw new Error('Failed to generate QR image for ' + code)
  const blob = await res.blob()
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Failed to read QR image'))
    reader.readAsDataURL(blob)
  })
}

export async function downloadQrStickerPdf(
  codes: string[],
  fileName: string
): Promise<void> {
  const unique: string[] = []
  const seen = new Set<string>()
  for (const c of codes) {
    if (!c || seen.has(c)) continue
    seen.add(c)
    unique.push(c)
  }
  if (!unique.length) throw new Error('No QR codes to put in the PDF')

  const items: QrStickerPdfItem[] = []
  // Sequential to avoid hammering the QR API
  for (const code of unique) {
    items.push({ code, imageDataUrl: await qrCodeToDataUrl(code) })
  }

  const instance = pdf(React.createElement(QrStickerBatchPdf, { items }) as any)
  const blob = await instance.toBlob()
  const url = URL.createObjectURL(blob)
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = `${fileName}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
}
